import React, { useMemo, useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Chess } from 'chess.js';
import {
  Chessboard,
  ChessboardDnDProvider,
  SparePiece,
} from 'react-chessboard';
import { isNativeApp } from '../../util/platformDetection';
import { GRAY } from '../../constants/colors';

const MobileFENBuilder = () => {
  const history = useHistory();
  const location = useLocation();
  const [fen, setFen] = useState("rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1");
  const [position, setPosition] = useState("rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1");
  const [boardOrientation, setBoardOrientation] = useState('white');
  const [pgnInput, setPgnInput] = useState("");
  const [pgnError, setPgnError] = useState("");
  const [fenError, setFenError] = useState("");
  const [moveList, setMoveList] = useState([]);
  const [isPgnMode, setIsPgnMode] = useState(false);
  const [gameResult, setGameResult] = useState({
    result: null,
    winner: null,
    reason: null
  });
  const [validationMessage, setValidationMessage] = useState({ text: "", isError: false });
  const [showGameModeChooser, setShowGameModeChooser] = useState(false);
  const [validatedData, setValidatedData] = useState(null);
  const [showFENModal, setShowFENModal] = useState(false);
  const [fenModalInput, setFenModalInput] = useState('');
  
  const chess = useMemo(() => new Chess(), []);

  // Initialize the chess instance with the starting FEN
  useEffect(() => {
    try {
      chess.load(fen);
      setPosition(chess.fen());
    } catch (e) {
      setFenError("Invalid FEN position");
    }
  }, []);

  // Effect to update isPgnMode whenever pgnInput changes
  useEffect(() => {
    setIsPgnMode(!!pgnInput.trim());
  }, [pgnInput]);

  // Effect to update board position when FEN changes
  useEffect(() => {
    try {
      if (fen && !isPgnMode) {
        chess.load(fen);
        setPosition(chess.fen());

        // Update board orientation based on whose turn it is in the FEN
        const fenParts = fen.split(' ');
        if (fenParts.length > 1) {
          const turn = fenParts[1];
          setBoardOrientation(turn === 'w' ? 'white' : 'black');
        }

        setFenError("");
      }
    } catch (e) {
      setFenError("Invalid FEN position");
    }
  }, [fen, isPgnMode]);

  const handleSparePieceDrop = (piece, targetSquare) => {
    // Prevent piece drops if in PGN mode
    if (isPgnMode) return false;

    const color = piece[0];
    const type = piece[1].toLowerCase();
    const success = chess.put(
      {
        type,
        color,
      },
      targetSquare,
    );
    if (success) {
      const newFen = chess.fen();
      setPosition(newFen);
      setFen(newFen);
    } else {
      alert(
        `The board already contains ${color === 'w' ? 'WHITE' : 'BLACK'} KING`,
      );
    }
    return success;
  };

  const handlePieceDrop = (sourceSquare, targetSquare, piece) => {
    // Prevent piece drops if in PGN mode
    if (isPgnMode) return false;

    const color = piece[0];
    const type = piece[1].toLowerCase();

    // this is hack to avoid chess.js bug, which I've fixed in the latest version https://github.com/jhlywa/chess.js/pull/426
    chess.remove(sourceSquare);
    chess.remove(targetSquare);
    const success = chess.put(
      {
        type,
        color,
      },
      targetSquare,
    );
    if (success) {
      const newFen = chess.fen();
      setPosition(newFen);
      setFen(newFen);
    }
    return success;
  };

  const handlePieceDropOffBoard = (sourceSquare) => {
    // Prevent piece drops if in PGN mode
    if (isPgnMode) return false;

    chess.remove(sourceSquare);
    const newFen = chess.fen();
    setPosition(newFen);
    setFen(newFen);
  };

  const handleFenInputChange = (e) => {
    if (isPgnMode) return; // Prevent FEN changes if in PGN mode
    setFen(e.target.value);
    setFenError("");
  };

  // FEN Modal handlers
  const handleOpenFENModal = () => {
    if (isPgnMode) return;
    setFenModalInput(fen);
    setShowFENModal(true);
  };

  const handleCloseFENModal = () => {
    setShowFENModal(false);
    setFenModalInput('');
  };

  const handleFENModalSubmit = () => {
    if (fenModalInput.trim()) {
      setFen(fenModalInput.trim());
      handleFenInputChange({ target: { value: fenModalInput.trim() } });
    }
    handleCloseFENModal();
  };

  const handlePgnInputChange = (e) => {
    setPgnInput(e.target.value);
    setPgnError("");

    // If PGN is being cleared, restore FEN as the active mode
    if (!e.target.value.trim()) {
      setIsPgnMode(false);
    } else {
      setIsPgnMode(true);
    }
  };

  // Handle flip board logic
  const handleFlipBoard = () => {
    // If in PGN mode, don't allow flipping
    if (isPgnMode) return;

    // Toggle board orientation
    const newOrientation = boardOrientation === 'white' ? 'black' : 'white';
    setBoardOrientation(newOrientation);

    // Update FEN to match - ensure the turn is of the side on the bottom
    const parts = position.split(' ');
    // If new orientation is white, turn should be 'w', if black, turn should be 'b'
    parts[1] = newOrientation === 'white' ? 'w' : 'b';
    const newFen = parts.join(' ');
    setPosition(newFen);
    setFen(newFen);
  };

  /**
   * Comprehensive PGN processor that creates moves identical to manual play
   * This ensures each move has proper structure including startSquare, endSquare, FEN, etc.
   * @param {string} pgnInput - The PGN string to process
   * @returns {Object} - Result object with success status and processed moves
   */
  const processComprehensivePGN = (pgnInput) => {
    try {
      // Create a fresh chess instance for processing
      const chess = new Chess();
      
      // Preprocess PGN string
      let processedPgn = pgnInput.replace(/\r?\n/g, ' ').trim();
      processedPgn = processedPgn.replace(/\s+/g, ' ');
      processedPgn = processedPgn.replace(/(\d+)\.([a-zA-Z0-9])/g, '$1. $2');

      // Try to load and validate PGN
      let isValidPGN = false;
      try {
        // Try the newer API first (loadPgn), then fall back to older API (load_pgn)
        if (typeof chess.loadPgn === 'function') {
          isValidPGN = chess.loadPgn(processedPgn, { sloppy: true });
        } else if (typeof chess.load_pgn === 'function') {
          isValidPGN = chess.load_pgn(processedPgn, { sloppy: true });
        } else {
          console.error('[MobileFENBuilder] No PGN loading method found on chess instance');
          throw new Error('PGN loading not supported by chess.js version');
        }
      } catch (e) {
        console.warn('[MobileFENBuilder] PGN validation failed:', e.message);
      }

      if (!isValidPGN) {
        console.error('[MobileFENBuilder] processComprehensivePGN - PGN INVALID');
        
        // Try manual PGN parsing as fallback
        try {
          const chess = new Chess();
          const comprehensiveMoves = [];
          
          // Extract moves from PGN manually
          // Remove headers and comments
          let moveText = processedPgn.replace(/\[.*?\]/g, '').replace(/\{.*?\}/g, '').trim();
          
          // Extract individual moves using regex
          // This regex matches move notation like e4, Nf3, O-O, Qxh7+, etc.
          const movePattern = /([a-hA-H]?[1-8]?[NBRQK]?[a-hA-H][1-8](?:=[NBRQ])?[+#]?|O-O(?:-O)?[+#]?)/g;
          const moves = [];
          let match;
          
          while ((match = movePattern.exec(moveText)) !== null) {
            const move = match[1];
            // Skip game result indicators
            if (!['1-0', '0-1', '1/2-1/2', '*'].includes(move)) {
              moves.push(move);
            }
          }
          
          if (moves.length === 0) {
            console.error('[MobileFENBuilder] No moves extracted from PGN');
            throw new Error('No moves could be extracted from PGN');
          }
          
          // Apply moves one by one
          for (let i = 0; i < moves.length; i++) {
            const moveNotation = moves[i];
            
            // Skip result indicators
            if (['1-0', '0-1', '1/2-1/2', '*'].includes(moveNotation)) {
              break;
            }
            
            const fenBeforeMove = chess.fen();
            
            try {
              const appliedMove = chess.move(moveNotation, { sloppy: true });
              
              if (appliedMove) {
                const fenAfterMove = chess.fen();
                
                // Create comprehensive move object directly with all required properties
                const moveObject = {
                  notation: moveNotation,
                  from: appliedMove.from,
                  to: appliedMove.to,
                  startSquare: appliedMove.from,
                  endSquare: appliedMove.to,
                  fen: fenAfterMove,
                  fenBefore: fenBeforeMove,
                  piece: appliedMove.piece,
                  captured: appliedMove.captured || null,
                  promotion: appliedMove.promotion || null,
                  content: '',
                  arrows: [],
                  highlights: []
                };
                
                comprehensiveMoves.push(moveObject);
              } else {
                console.error(`[MobileFENBuilder] Manual parsing failed at move: ${moveNotation}`);
                break;
              }
            } catch (e) {
              console.error(`[MobileFENBuilder] Manual parsing error at move ${moveNotation}:`, e);
              break;
            }
          }
          
          if (comprehensiveMoves.length > 0) {
            // Analyze game result
            const gameResult = {
              result: "unfinished",
              winner: null,
              reason: "ongoing"
            };

            if (chess.game_over()) {
              if (chess.in_checkmate()) {
                gameResult.result = "checkmate";
                gameResult.winner = chess.turn() === 'w' ? 'black' : 'white';
                gameResult.reason = "checkmate";
              } else if (chess.in_draw()) {
                gameResult.result = "draw";
                if (chess.in_stalemate()) {
                  gameResult.reason = "stalemate";
                } else if (chess.insufficient_material()) {
                  gameResult.reason = "insufficient material";
                } else {
                  gameResult.reason = "fifty move rule or repetition";
                }
              }
            }
            
            return {
              success: true,
              moves: comprehensiveMoves,
              gameResult,
              finalFEN: chess.fen()
            };
          } else {
            console.error('[MobileFENBuilder] Manual parsing failed - no moves were successfully applied');
          }
          
        } catch (manualError) {
          console.error('[MobileFENBuilder] Manual parsing also failed:', manualError);
        }
        
        return {
          success: false,
          error: "Invalid PGN format",
          moves: []
        };
      }

      // Get the move history from chess.js
      const moveHistory = chess.history({ verbose: true });

      // Now rebuild moves step by step to match manual play structure
      const tempChess = new Chess();
      const comprehensiveMoves = [];

      for (let i = 0; i < moveHistory.length; i++) {
        const chessMove = moveHistory[i];
        
        // Get the FEN before this move
        const fenBeforeMove = tempChess.fen();
        
        // Apply the move to our temp chess instance
        const appliedMove = tempChess.move(chessMove.san, { sloppy: true });
        
        if (appliedMove) {
          // Create comprehensive move object directly with all required properties
          const moveObject = {
            notation: chessMove.san,
            from: appliedMove.from,
            to: appliedMove.to,
            startSquare: appliedMove.from,
            endSquare: appliedMove.to,
            fen: tempChess.fen(), // FEN after the move
            fenBefore: fenBeforeMove, // FEN before the move
            piece: appliedMove.piece,
            captured: appliedMove.captured || null,
            promotion: appliedMove.promotion || null,
            content: '',
            arrows: [],
            highlights: []
          };
          
          comprehensiveMoves.push(moveObject);
        } else {
          console.error(`[MobileFENBuilder] Failed to apply move: ${chessMove.san}`);
          break;
        }
      }

      // Analyze game result
      const gameResult = {
        result: "unfinished",
        winner: null,
        reason: "ongoing"
      };

      if (chess.game_over()) {
        if (chess.in_checkmate()) {
          gameResult.result = "checkmate";
          gameResult.winner = chess.turn() === 'w' ? 'black' : 'white';
          gameResult.reason = "checkmate";
        } else if (chess.in_draw()) {
          gameResult.result = "draw";
          if (chess.in_stalemate()) {
            gameResult.reason = "stalemate";
          } else if (chess.insufficient_material()) {
            gameResult.reason = "insufficient material";
          } else {
            gameResult.reason = "fifty move rule or repetition";
          }
        }
      }

      return {
        success: true,
        moves: comprehensiveMoves,
        gameResult,
        finalFEN: chess.fen()
      };

    } catch (error) {
      console.error('[MobileFENBuilder] processComprehensivePGN - ERROR:', error);
      return {
        success: false,
        error: error.message || "Unknown error during PGN processing",
        moves: []
      };
    }
  };

  const handleLoadPgn = () => {
    try {
      // Reset errors
      setPgnError("");
      setValidationMessage({ text: "", isError: false });

      // If PGN is empty, show error
      if (!pgnInput.trim()) {
        const error = "Position Failed";
        console.error("PGN parsing failed: Empty input");
        setPgnError(error);
        setValidationMessage({ text: error, isError: true });
        return false;
      }

      // Use the new comprehensive PGN processor
      const comprehensiveResult = processComprehensivePGN(pgnInput.trim());

      if (comprehensiveResult.success) {
        // Store the comprehensive result for later use
        chess.load(comprehensiveResult.finalFEN);
        setFen(comprehensiveResult.finalFEN);
        setPosition(comprehensiveResult.finalFEN);
        setGameResult(comprehensiveResult.gameResult);

        // Create moveList for display compatibility
        const moveList = comprehensiveResult.moves.map((move, index) => ({
          move: move.notation,
          content: move.content || '',
          fen: move.fen,
          moveNumber: Math.floor(index / 2) + 1,
          color: index % 2 === 0 ? 'white' : 'black'
        }));
        setMoveList(moveList);

        // Store comprehensive moves for retrieval
        window._lastComprehensiveResult = comprehensiveResult;

        return true;
      } else {
        // Clear any stored comprehensive result
        window._lastComprehensiveResult = null;

        // Reset game result for fallback strategy
        setGameResult({
          result: "unknown",
          winner: null,
          reason: "pgn parsing failed"
        });

        const errorMsg = "Position Failed";
        console.error("INVALID PGN - NO VALID MOVES FOUND");
        setPgnError(errorMsg);
        setValidationMessage({ text: errorMsg, isError: true });
        return false;
      }
    } catch (e) {
      const errorMsg = "Position Failed";
      console.error("Unexpected error during PGN parsing:", e);
      setPgnError(errorMsg);
      setValidationMessage({ text: errorMsg, isError: true });
      return false;
    }
  };

  const handleSetFen = () => {
    // Only allow setting FEN if not in PGN mode
    if (isPgnMode) return false;

    try {
      setValidationMessage({ text: "", isError: false });

      // Basic validation for empty FEN
      if (!fen.trim()) {
        const errorMsg = "Position Failed";
        setFenError(errorMsg);
        setValidationMessage({ text: errorMsg, isError: true });
        return false;
      }

      chess.load(fen);
      const validFen = chess.fen();

      setPosition(validFen);
      setFenError("");

      // Check for other conditions like missing kings
      const fenParts = validFen.split(' ');
      const piecePlacement = fenParts[0];

      if (!piecePlacement.includes('K') || !piecePlacement.includes('k')) {
        const errorMsg = "Position Failed";
        setFenError(errorMsg);
        setValidationMessage({ text: errorMsg, isError: true });
        return false;
      }

      return true;
    } catch (e) {
      const errorMsg = "Position Failed";
      console.error("FEN validation failed:", e.message || e);
      setFenError(errorMsg);
      setValidationMessage({ text: errorMsg, isError: true });
      return false;
    }
  };

  const handleValidateAndProceed = () => {
    // Clear any previous validation messages
    setValidationMessage({ text: "", isError: false });

    // Validate based on active mode
    let isValid = false;

    if (isPgnMode) {
      // For PGN: validate and process in one step
      isValid = handleLoadPgn();

      // If there's an error stored in state, don't proceed
      if (pgnError) {
        setValidationMessage({ text: "Position Failed", isError: true });
        isValid = false;
      }
    } else {
      // For FEN: validate position
      isValid = handleSetFen();

      // If there's an error stored in state, don't proceed
      if (fenError) {
        setValidationMessage({ text: "Position Failed", isError: true });
        isValid = false;
      }
    }

    // Only proceed if validation passed
    if (isValid) {
      // Hide success message - just proceed silently
      setValidationMessage({ text: "", isError: false });

      // Store validated data for later use
      if (isPgnMode) {
        const comprehensiveMoves = window._lastComprehensiveResult?.moves || null;
        // console.log('🔥 [MobileFENBuilder] Storing PGN validation data:', {
        //   comprehensiveMovesLength: comprehensiveMoves?.length,
        //   moveListLength: moveList?.length,
        //   gameResult
        // });
        
        setValidatedData({
          type: 'pgn',
          pgnInput,
          moveList,
          gameResult,
          comprehensiveMoves
        });
      } else {
        // console.log('🔥 [MobileFENBuilder] Storing FEN validation data:', {
        //   position,
        //   gameResult
        // });
        
        setValidatedData({
          type: 'fen',
          position,
          gameResult
        });
      }

      // Check if we came from "Create a Puzzle" button
      const puzzleIntent = location.state?.puzzleMode;
      if (puzzleIntent) {
        // console.log('🔥 [FENBuilder] Puzzle intent detected, skipping modal and auto-selecting Puzzle mode');
        
        // Navigate directly with puzzle mode - use the data we just validated
        const tags = ['Puzzle'];
        const currentValidatedData = isPgnMode ? {
          type: 'pgn',
          pgnInput,
          moveList,
          gameResult,
          comprehensiveMoves: window._lastComprehensiveResult?.moves || null
        } : {
          type: 'fen',
          position,
          gameResult
        };
        
        if (currentValidatedData.type === 'pgn') {
          // PGN flow
          history.push('/Analysis', {
            pgnData: currentValidatedData.pgnInput,
            moveList: currentValidatedData.moveList,
            gameResult: currentValidatedData.gameResult,
            comprehensiveMoves: currentValidatedData.comprehensiveMoves,
            autoTags: tags,
            gameMode: 'Puzzle',
            fromFENBuilder: true,
            boardOrientation: boardOrientation
          });
        } else {
          // FEN flow
          history.push('/Analysis', {
            customFEN: currentValidatedData.position,
            gameResult: currentValidatedData.gameResult,
            autoTags: tags,
            gameMode: 'Puzzle',
            fromFENBuilder: true,
            boardOrientation: boardOrientation
          });
        }
      } else {
        // Show game mode chooser for manual setup
        setShowGameModeChooser(true);
      }
    }
  };

  const handleGameModeSelect = (mode) => {
    if (!validatedData) return;

    // Auto-tag based on mode selection
    const tags = mode === 'Puzzle' ? ['Puzzle'] : ['Lesson'];

    if (validatedData.type === 'pgn') {
      // PGN flow - navigate to add game with PGN data and moves
      // console.log('🔥 [MobileFENBuilder] Navigating with PGN data:', {
      //   comprehensiveMovesLength: validatedData.comprehensiveMoves?.length,
      //   gameResult: validatedData.gameResult,
      //   moveListLength: validatedData.moveList?.length
      // });
      
      history.push('/Analysis', {
        pgnData: validatedData.pgnInput,
        moveList: validatedData.moveList,
        gameResult: validatedData.gameResult,
        comprehensiveMoves: validatedData.comprehensiveMoves,
        autoTags: tags,
        gameMode: mode,
        fromFENBuilder: true,
        boardOrientation: boardOrientation
      });
    } else {
      // FEN flow - navigate to add game with custom starting position
      // console.log('🔥 [MobileFENBuilder] Navigating with FEN data:', {
      //   customFEN: validatedData.position,
      //   gameResult: validatedData.gameResult
      // });
      
      history.push('/Analysis', {
        customFEN: validatedData.position,
        gameResult: validatedData.gameResult,
        autoTags: tags,
        gameMode: mode,
        fromFENBuilder: true,
        boardOrientation: boardOrientation
      });
    }
  };

  const pieces = [
    'wP',
    'wN',
    'wB',
    'wR',
    'wQ',
    'wK',
    'bP',
    'bN',
    'bB',
    'bR',
    'bQ',
    'bK',
  ];

  if (showGameModeChooser) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        background: 'linear-gradient(145deg, #1a1a1a, #000000)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px',
        paddingTop: isNativeApp() ? 'calc(env(safe-area-inset-top, 44px) + 20px)' : '20px',
        boxSizing: 'border-box'
      }}>
        <div style={{
          color: 'gold',
          fontSize: '28px',
          fontWeight: 'bold',
          textAlign: 'center',
          borderBottom: '2px solid gold',
          padding: '20px',
          marginBottom: '30px',
          letterSpacing: '2px'
        }}>
          Game Mode
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%', maxWidth: '500px' }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
            padding: '20px',
            borderRadius: '10px',
            color: 'white',
            border: '2px solid #00f2ea'
          }}>
            <button
              style={{
                padding: '20px',
                fontSize: '24px',
                fontWeight: 'bold',
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: '#00f2ea',
                border: '3px solid #00f2ea',
                color: 'black',
                transition: 'all 0.3s ease'
              }}
              onClick={() => handleGameModeSelect("Puzzle")}
            >
              Puzzle
            </button>
            <div style={{ textAlign: 'center' }}>
              A sequence of forcing moves.
            </div>
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
            padding: '20px',
            borderRadius: '10px',
            color: 'white',
            border: `2px solid ${GRAY}`
          }}>
            <button
              style={{
                padding: '20px',
                fontSize: '24px',
                fontWeight: 'bold',
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: GRAY,
                border: `3px solid ${GRAY}`,
                color: 'white',
                transition: 'all 0.3s ease'
              }}
              onClick={() => handleGameModeSelect("Lesson")}
            >
              Lesson
            </button>
            <div style={{ textAlign: 'center' }}>
              Build unlimited moves and variations.
            </div>
          </div>
        </div>

        <button
          style={{
            marginTop: '30px',
            padding: '15px 30px',
            fontSize: '18px',
            fontWeight: 'bold',
            borderRadius: '8px',
            backgroundColor: '#ff6b6b',
            color: 'white',
            border: '3px solid #ff6b6b',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onClick={() => {
            setShowGameModeChooser(false);
            setValidatedData(null);
          }}
        >
          BACK TO EDITOR
        </button>
      </div>
    );
  }

  return (
    <div style={{
      width: '100vw',
      height: 'calc(100dvh - 55px)', // Account for bottom navigation
      backgroundColor: '#1a1a1a',
      display: 'flex',
      flexDirection: 'column',
      padding: '10px',
      paddingTop: 'calc(env(safe-area-inset-top) + 10px)', // iPhone notch safe area + padding
      paddingBottom: 'env(safe-area-inset-bottom)', // iPhone gesture area
      boxSizing: 'border-box',
      overflowY: 'auto'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#1a1a1a',
        borderBottom: '1px solid #333',
        padding: '12px 20px',
        position: 'relative',
        flexShrink: 0,
        marginBottom: '10px'
      }}>
        <h3 style={{ 
          color: 'gold', 
          margin: 0, 
          fontSize: '18px',
          textAlign: 'center',
          fontWeight: 'bold' 
        }}>
         <i class="fa-solid fa-chess-board"></i>  Drag & Drop 
         {location.state?.puzzleMode ? " Puzzle Setup" : " Setup"} 
        </h3>
        <button
          onClick={() => history.goBack()}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            color: '#888',
            fontSize: '14px',
            cursor: 'pointer',
            padding: '0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            position: 'absolute',
            left: '5%',
            top: '40%'
          }}
        >
          <i className="fa-solid fa-arrow-left" style={{ fontSize: '16px' }}></i>
        </button>
       
        <div style={{ width: '80px' }}></div> {/* Spacer for centering */}
      </div>

      {/* Board and Pieces Container */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        marginBottom: '8px'
      }}>
        <ChessboardDnDProvider>
          {/* Black Pieces */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '4px',
            opacity: isPgnMode ? 0.5 : 1,
            padding: '4px',
            backgroundColor: '#f5f5f5',
            borderRadius: '6px',
            border: '1px solid #ddd'
          }}>
            {pieces.slice(6, 12).map((piece) => (
              <SparePiece
                key={piece}
                piece={piece}
                width={34}
                dndId="MobileBoardEditor"
              />
            ))}
          </div>

          {/* Chessboard */}
          <div style={{ marginBottom: '4px' }}>
            <Chessboard
              customDarkSquareStyle={{
                background: 'linear-gradient(to left, #3066BE, #4A7DD9)',
              }}
              customLightSquareStyle={{
                background: 'linear-gradient(to left, #fffff0, #f5e4c3)',
              }}
              id="MobileBoardEditor"
              position={position}
              boardOrientation={boardOrientation}
              onSparePieceDrop={isPgnMode ? null : handleSparePieceDrop}
              onPieceDrop={isPgnMode ? null : handlePieceDrop}
              boardWidth={Math.min(window.innerWidth - 40, 320)}
              onPieceDropOffBoard={isPgnMode ? null : handlePieceDropOffBoard}
              dropOffBoardAction="trash"
              customBoardStyle={{
                border: '3px solid black',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                opacity: isPgnMode ? 0.9 : 1,
              }}
            />
          </div>

          {/* White Pieces */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            opacity: isPgnMode ? 0.5 : 1,
            padding: '4px',
            backgroundColor: '#2a2a2a',
            borderRadius: '6px',
            border: '1px solid #555'
          }}>
            {pieces.slice(0, 6).map((piece) => (
              <SparePiece
                key={piece}
                piece={piece}
                width={34}
                dndId="MobileBoardEditor"
              />
            ))}
          </div>
        </ChessboardDnDProvider>
        
        {/* Button Container matching board width */}
        <div style={{ 
          width: Math.min(window.innerWidth - 40, 320),
          marginTop: '10px'
        }}>
          {/* Board Control Buttons */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <button
              style={{
                flex: 1,
                padding: '10px',
                backgroundColor: position.split(' ')[1] === 'w' ? '#f0f0f0' : '#2a2a2a',
                color: position.split(' ')[1] === 'w' ? 'black' : 'white',
                fontWeight: 'bold',
                borderRadius: '6px',
                border: 'none',
                fontSize: '14px',
                cursor: isPgnMode ? 'not-allowed' : 'pointer',
                opacity: isPgnMode ? 0.6 : 1,
              }}
              onClick={handleFlipBoard}
              disabled={isPgnMode}
            >
              {position.split(' ')[1] === 'w' ? 'WHITE' : 'BLACK'}
            </button>
            <button
              style={{
                flex: 1,
                padding: '10px',
                fontSize: '14px',
                fontWeight: 'bold',
                border: 'none',
                color: 'white',
                borderRadius: '6px',
                backgroundColor: '#4a5568',
                cursor: isPgnMode ? 'not-allowed' : 'pointer',
                opacity: isPgnMode ? 0.6 : 1,
              }}
              onClick={() => {
                if (isPgnMode) return;
                chess.clear();
                const newFen = chess.fen();
                setPosition(newFen);
                setFen(newFen);
              }}
              disabled={isPgnMode}
            >
              CLEAR
            </button>
            <button
              style={{
                flex: 1,
                padding: '10px',
                fontSize: '14px',
                fontWeight: 'bold',
                border: 'none',
                color: 'white',
                borderRadius: '6px',
                backgroundColor: '#4a5568',
                cursor: isPgnMode ? 'not-allowed' : 'pointer',
                opacity: isPgnMode ? 0.6 : 1,
              }}
              onClick={() => {
                if (isPgnMode) return;
                chess.reset();
                const newFen = chess.fen();
                setPosition(newFen);
                setFen(newFen);
              }}
              disabled={isPgnMode}
            >
              RESET
            </button>
            <button
              style={{
                flex: 1,
                padding: '10px',
                fontSize: '14px',
                fontWeight: 'bold',
                border: 'none',
                color: 'white',
                borderRadius: '6px',
                backgroundColor: '#4a5568',
                cursor: isPgnMode ? 'not-allowed' : 'pointer',
                opacity: isPgnMode ? 0.6 : 1
              }}
              onClick={handleOpenFENModal}
              disabled={isPgnMode}
            >
              FEN
            </button>
          </div>

          {/* Game Result Indicator */}
          {gameResult.result && gameResult.result !== "unknown" && (
            <div style={{
              marginBottom: '8px',
              padding: '6px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 'bold',
              border: '2px solid black',
              backgroundColor: gameResult.result === 'checkmate' ? '#ffeb3b' :
                gameResult.result === 'draw' ? '#90caf9' :
                  gameResult.result === 'resigned' ? '#ff9800' : '#e0e0e0',
              color: 'black',
              textAlign: 'center'
            }}>
              {gameResult.result === 'checkmate' && (
                <span>Checkmate: {gameResult.winner === 'white' ? 'White' : 'Black'} wins</span>
              )}
              {gameResult.result === 'draw' && (
                <span>Draw: {gameResult.reason === 'stalemate' ? 'Stalemate' :
                  gameResult.reason === 'insufficient material' ? 'Insufficient Material' :
                    gameResult.reason === 'threefold repetition' ? 'Threefold Repetition' :
                      gameResult.reason === 'fifty move rule' ? 'Fifty-move Rule' :
                        'Agreement'}</span>
              )}
              {gameResult.result === 'unfinished' && (
                <span>Unfinished game</span>
              )}
            </div>
          )}

          {/* Load Position Button */}
          <button
            style={{
              padding: '16px',
              fontSize: '18px',
              fontWeight: 'bold',
              backgroundColor: '#ffd700',
              color: 'black',
              border: '3px solid gold',
              borderRadius: '8px',
              cursor: 'pointer',
              width: '100%',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
              transition: 'all 0.2s ease'
            }}
            onClick={handleValidateAndProceed}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'scale(0.98)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {isPgnMode ? 'LOAD PGN' : 'LOAD POSITION'}
          </button>
        </div>
      </div>

      {/* Validation Message */}
      {validationMessage.text && validationMessage.isError && (
        <div style={{
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'bold',
          backgroundColor: '#ffcdd2',
          color: '#b71c1c',
          border: '1px solid #b71c1c',
          textAlign: 'center',
          marginBottom: '6px',
          maxWidth: '200px',
          margin: '0 auto 6px auto'
        }}>
          {validationMessage.text}
        </div>
      )}

      {/* FEN Input Modal */}
      {showFENModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            borderRadius: '8px',
            padding: '20px',
            maxWidth: '90vw',
            width: '400px',
            border: '1px solid #444',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
          }}>
            {/* Header */}
            <h2 style={{
              textAlign: 'center',
              color: 'gold',
              marginTop: 0,
              marginBottom: '20px',
              fontSize: '24px',
              fontWeight: 'bold'
            }}>
              Enter FEN
            </h2>

            {/* Instructions */}
            <div style={{
              color: '#ccc',
              fontSize: '14px',
              marginBottom: '15px',
              lineHeight: '1.4',
              textAlign: 'center'
            }}>
              Enter a valid FEN string:
            </div>

            {/* FEN Input */}
            <textarea
              value={fenModalInput}
              onChange={(e) => setFenModalInput(e.target.value)}
              placeholder="rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
              style={{
                width: '100%',
                height: '100px',
                padding: '12px',
                fontSize: '14px',
                backgroundColor: '#333',
                color: 'white',
                border: '1px solid #666',
                borderRadius: '8px',
                resize: 'vertical',
                outline: 'none',
                fontFamily: 'monospace',
                boxSizing: 'border-box',
                marginBottom: '20px'
              }}
            />

            {/* Buttons */}
            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center'
            }}>
              <button
                onClick={handleCloseFENModal}
                style={{
                  padding: '12px 20px',
                  backgroundColor: 'transparent',
                  color: '#888',
                  border: '1px solid #888',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleFENModalSubmit}
                disabled={!fenModalInput.trim()}
                style={{
                  padding: '12px 20px',
                  backgroundColor: fenModalInput.trim() ? 'gold' : '#555',
                  color: fenModalInput.trim() ? 'black' : '#ccc',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: fenModalInput.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                Apply FEN
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileFENBuilder;